import {
	type InvestmentActionHistorySummaryEntry,
	type InvestmentActionHistorySummaryEntryInvestmentActionHistoryEnum,
	type InvestmentSummary,
	InvestmentExportControllerApiFactory,
	InvestmentReportsControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { useApiGen } from "$root/api/hooks";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import type { DefaultTemplateId } from "$root/components/EvolvedPrint/configuration/hooks/useExtractReports";
import {
	type InlineTextFormImperativeHandles,
	type InlineTextFormMode,
	InlineTextForm,
} from "$root/components/smart-text-input/InlineTextForm";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { UserCircle, UserCircleWithTooltip } from "$root/functional-areas/acl/UserCircle";
import { userCirclePaletteByRole } from "$root/functional-areas/acl/UsersList";
import { aclByArea, roleFromAcl } from "$root/functional-areas/acl/checkers/all";
import { buildCompositionExportConfig } from "$root/functional-areas/instruments/export";
import { downloadSinglePdf } from "$root/functional-areas/pdf";
import { useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
	portfolioWidgetMissingDataReason,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { type ContextContent, downloadContentDisposition, qualifier, withContext } from "$root/utils";
import type { MaybeAsync } from "@mdotm/utils/promise";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	CollapsibleBase,
	Dialog,
	DialogFooter,
	ScrollWrapper,
	type StylableProps,
	Text,
} from "@mdotm/shadcn/legacy/components";
import {
	type ImperativeHandleRefProps,
	type SpawnResult,
	adaptAnimatedNodeProvider,
	spawn,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, stableEmptyArray } from "@mdotm/shadcn/legacy/utils";
import { groupBy } from "@mdotm/utils/collections";
import { useCallback, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { PortfolioContext } from "../contexts/portfolio";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import {
	type DropdownDropdownItem,
	DropdownMenu,
	type DropdownTriggerProps,
	Button,
	IconButton,
} from "@mdotm/shadcn/mdotm/components";
import { buildWidgetExportConfig } from "./widget-export";

function HistoryDownloadTrigger({ state: _state, ...props }: DropdownTriggerProps): React.JSX.Element {
	return (
		<span className="absolute top-[6px]" {...props}>
			<IconButton icon="Dowload" type="button" className="p-0!" />
		</span>
	);
}

const transitionDurationMs = 400;

export type PortfolioHistoryEventType = InvestmentActionHistorySummaryEntryInvestmentActionHistoryEnum;

export type NotePayload = {
	author: { name?: string; surname?: string; email?: string };
	note: string;
	date: string;
	status: "MODIFIED" | "CREATED";
};

export type PortfolioHistoryEvent = {
	date: string;
	type: PortfolioHistoryEventType;
	id: string;
	author?: RichAccessControl;
	note?: NotePayload;
	hasUniverseSnapshot?: boolean;
	hasReferenceSnapshot?: boolean;
	hasBenchmarkSnapshot?: boolean;
	changesAvailable?: boolean;
	marketViewEmbedded?: boolean;
	subPortfolios?: Array<{
		title: string;
		weight: number;
		id: string;
	}>;
};

export type NoteEvent = NotePayload & {
	id: string;
};

function isNoteEvent(event: PortfolioHistoryEvent | NoteEvent): event is NoteEvent {
	return !("type" in event);
}
function isPortfolioHistoryEvent(event: PortfolioHistoryEvent | NoteEvent): event is PortfolioHistoryEvent {
	return "type" in event;
}

function NoteInlineForm({
	className,
	style,
	notePayload,
	mode = "view",
	onEdit,
	onDelete,
	canEdit,
	handleRef,
}: {
	notePayload?: NotePayload;
	mode?: InlineTextFormMode;
	onEdit: MaybeAsync<void, [string]>;
	onDelete: MaybeAsync<void>;
	canEdit: boolean;
} & StylableProps &
	ImperativeHandleRefProps<InlineTextFormImperativeHandles>) {
	const { formatDate } = useLocaleFormatters();
	return (
		<div className={toClassName(className)} style={style}>
			{notePayload && (
				<>
					<Text type="Body/M/Bold" color={themeCSSVars.palette_N800}>
						Note
					</Text>{" "}
					<Text type="Body/M/Book" color={themeCSSVars.palette_N500}>
						({notePayload.status === "CREATED" ? "Created" : "Edited"} by {notePayload.author.name}{" "}
						{notePayload.author.surname} on {formatDate(notePayload.date)})
					</Text>
				</>
			)}
			<InlineTextForm
				handleRef={handleRef}
				noDataText=""
				rows={5}
				className="w-full flex items-start min-h-[56px] py-0!"
				mode={mode}
				value={notePayload?.note}
				canEdit={canEdit}
				onEdit={onEdit}
				onDelete={onDelete}
			/>
		</div>
	);
}

function NoteCollapsible({
	expand,
	...forward
}: {
	notePayload?: NotePayload;
	expand: boolean;
	onEdit: MaybeAsync<void, [string]>;
	onDelete: MaybeAsync<void>;
	canEdit: boolean;
} & ImperativeHandleRefProps<InlineTextFormImperativeHandles>) {
	return (
		<CollapsibleBase expand={expand} transitionDuration={transitionDurationMs}>
			<NoteInlineForm
				mode={forward.notePayload || !forward.canEdit ? "view" : "edit"}
				className={`px-1.5 py-1 bg-[var(--palette-N20)]`}
				{...forward}
			/>
		</CollapsibleBase>
	);
}

export const typeToStringMap: Record<PortfolioHistoryEventType, { title: string; downloadText?: string }> = {
	UPLOAD: { title: "Upload", downloadText: "Composition" },
	CREATION: { title: "Creation", downloadText: "Composition" },
	ENHANCEMENT: { title: "Proposal accepted", downloadText: "Composition & trades" },
	OPTIMIZATION: { title: "Optimization accepted", downloadText: "Composition & trades" },
	EDIT_PTF_SETTINGS: { title: "Portfolio Settings Edited", downloadText: "Composition & trades" },
	EDIT_REFERENCE: { title: "Reference Edited", downloadText: "Reference download" },
	EDIT_BENCHMARK: { title: "Benchmark Edited" },
	EDIT_UNIVERSE: { title: "Universe Edited", downloadText: "Universe download" },
	EDIT: { title: "Edit composition", downloadText: "Composition" },
};
const mixedPortfolioTitle = (type: PortfolioHistoryEventType) =>
	`${typeToStringMap[type]?.title ?? "-"} based on mixing instruments and the following portfolio compositions:`;

/** The download handlers an entry's menu can offer. */
const historyEntryDownloadKeys = [
	"onDownload",
	"onDownloadSphereFormat",
	"onDownloadUniverse",
	"onDownloadMarketView",
	"onDownloadReference",
	"onDownloadEditReference",
	"onDownloadBenchmark",
	"onDownloadEditBenchmark",
	"onDownloadEditMarketView",
	"onDownloadEditedSphere",
	"onDownloadEditUniverse",
	"onDownloadChanges",
] satisfies Array<keyof PortfolioHistoryListProps>;
type HistoryEntryDownloads = Pick<PortfolioHistoryListProps, (typeof historyEntryDownloadKeys)[number]>;

/** One history entry's download menu: which files its type offers, and the export each one calls. */
function historyEntryActions(
	event: PortfolioHistoryEvent,
	portfolioUuid: string | undefined,
	downloads: HistoryEntryDownloads,
): DropdownDropdownItem[] {
	const item = (
		label: string,
		download: HistoryEntryDownloads[keyof HistoryEntryDownloads],
		qualifierLabel = label,
	): DropdownDropdownItem => ({
		label,
		onClickAsync: async () => download?.(event),
		"data-qualifier": `PortfolioHistory/Portfolio(${portfolioUuid})/History(${event.id})/DropdownItem(${qualifierLabel})`,
	});
	const when = (shown: boolean | undefined, entry: DropdownDropdownItem) => (shown ? [entry] : []);
	const changes = when(event.changesAvailable, item("Changes", downloads.onDownloadChanges));

	switch (event.type) {
		case "EDIT_PTF_SETTINGS":
			return [
				item("Sphere template", downloads.onDownloadEditedSphere),
				...changes,
				...when(event.hasUniverseSnapshot, item("Universe", downloads.onDownloadEditUniverse, "Edit Universe")),
				...when(event.hasReferenceSnapshot, item("Reference", downloads.onDownloadEditReference, "Edit Reference")),
				...when(event.hasBenchmarkSnapshot, item("Benchmark", downloads.onDownloadEditBenchmark, "Edit Benchmark")),
				...when(!event.marketViewEmbedded, item("Market View", downloads.onDownloadEditMarketView, "Edit Market View")),
			];
		case "EDIT_REFERENCE":
			return [item("Reference", downloads.onDownloadEditReference, "Edit Reference"), ...changes];
		case "EDIT_BENCHMARK":
			return [
				...when(event.hasBenchmarkSnapshot, item("Benchmark", downloads.onDownloadEditBenchmark, "Edit Benchmark")),
				...changes,
			];
		case "EDIT_UNIVERSE":
			return [item("Universe", downloads.onDownloadEditUniverse, "Edit Universe"), ...changes];
		default: {
			const downloadText = typeToStringMap[event.type]?.downloadText ?? "";
			return [
				// A nested entry offers its composition in the sleeve list instead.
				...when(!event.subPortfolios, item(downloadText, downloads.onDownload)),
				...changes,
				item("Sphere template", downloads.onDownloadSphereFormat),
				...when(event.hasUniverseSnapshot, item("Universe", downloads.onDownloadUniverse)),
				...when(event.hasReferenceSnapshot, item("Reference", downloads.onDownloadReference, "Reference template")),
				...when(event.hasBenchmarkSnapshot, item("Benchmark", downloads.onDownloadBenchmark)),
				...when(!event.marketViewEmbedded, item("Market View", downloads.onDownloadMarketView)),
			];
		}
	}
}

function EventViewer({
	event,
	user,
	portfolio,
	onDownloadComposition,
	onDownloadMixItem,
	variant,
	onEdit,
	onDelete,
	downloads,
}: {
	event: PortfolioHistoryEvent | NoteEvent;
	user: { id: string };
	portfolio: InvestmentSummary;
	onDownloadComposition?(event: PortfolioHistoryEvent, format: "xls" | "pdf"): Promise<void>;
	onDownloadMixItem?(event: PortfolioHistoryEvent, mixItemId: string): Promise<void>;
	downloads: HistoryEntryDownloads;
	variant: "collapsible" | "expanded";
	onEdit: MaybeAsync<void, [string]>;
	onDelete: MaybeAsync<void>;
}): React.JSX.Element {
	const { formatDate } = useLocaleFormatters();
	const [expandNote, setExpandNote] = useState(variant === "expanded");

	const notePayload = useMemo(() => (isNoteEvent(event) ? event : event.note), [event.note, event]);
	const [showExpandedEditor, setShowExpandedEditor] = useState(false);
	const inlineFormHandlesRef = useRef<InlineTextFormImperativeHandles | null>(null);
	const downloadActions = isPortfolioHistoryEvent(event) ? historyEntryActions(event, portfolio.uuid, downloads) : [];
	const downloadMenu = (
		<DropdownMenu
			Trigger={HistoryDownloadTrigger}
			extraTriggerProps={{
				"data-qualifier": `PortfolioHistory/Portfolio(${portfolio.uuid})/History(${event.id})`,
			}}
			actions={downloadActions}
		/>
	);
	const nestedDownloadMenu = isPortfolioHistoryEvent(event) && event.subPortfolios ? downloadMenu : false;

	return (
		<>
			<div className="flex">
				<div className="min-h-[40px] w-[40px] min-w-[40px] pt-1.5">
					<Text type="Body/S/Book" className="ml-2" color={themeCSSVars.palette_N400}>
						{formatDate(event.date, { omitYear: true })}
					</Text>
				</div>
				<div className="min-h-[40px] w-[40px] min-w-[40px] px-[10px] pt-1">
					{event.author ? (
						<UserCircleWithTooltip
							position="left"
							variant="name"
							palette={generateUserCirclePalette({ event, user, author: event.author, portfolio })}
							firstName={event.author.name ?? "-"}
							lastName={event.author.surname ?? "-"}
							size="small"
						/>
					) : (
						<UserCircle palette="dashed" variant="add" className="invisible" />
					)}
				</div>
				<div className="min-h-[40px] min-w-0 grow">
					<div className="min-w-0 flex items-start relative">
						<div className="grow flex space-x-2 flex-wrap pt-2">
							<div
								className={toClassName({
									"text-[color:var(--palette-N800)]": true,
									"flex items-center": Boolean(nestedDownloadMenu),
								})}
							>
								<Text type="Body/M/Bold">
									{isNoteEvent(event)
										? "New note"
										: event.subPortfolios
											? mixedPortfolioTitle(event.type)
											: (typeToStringMap[event.type]?.title ?? "-")}
								</Text>
								{nestedDownloadMenu && (
									// h-8 centres the trigger, which sits at top-[6px], on the wrapped heading.
									<span className="relative w-5 h-8 ml-2 shrink-0">{nestedDownloadMenu}</span>
								)}
							</div>

							{isPortfolioHistoryEvent(event) &&
								(event.subPortfolios ? (
									<div className="w-full">
										{onDownloadComposition && (
											<>
												<Button onClickAsync={() => onDownloadComposition(event, "xls")} unstyled className="underline">
													{(state) => (
														<Text
															type="Body/S/Link"
															color={themeCSSVars.palette_N400}
															className={{
																"animate-pulse": state.kind === "loading",
															}}
														>
															Composition (xls)
														</Text>
													)}
												</Button>
												<br />
												<Button onClickAsync={() => onDownloadComposition(event, "pdf")} unstyled className="underline">
													{(state) => (
														<Text
															type="Body/S/Link"
															color={themeCSSVars.palette_N400}
															className={{
																"animate-pulse": state.kind === "loading",
															}}
														>
															Composition & exposure contribution (pdf)
														</Text>
													)}
												</Button>
											</>
										)}
										<ul className="pt-1 -ml-2 mb-1">
											<ForEach collection={event.subPortfolios}>
												{(subPortfolio) => (
													<li>
														<span className="text-[12px] font-bold">{subPortfolio.title}</span>{" "}
														{/* <span className="text-[12px] mr-[16px]">({formatNumber(mixItem.weight, 0)}%)</span> */}
														{onDownloadMixItem && (
															<Button
																onClickAsync={() => onDownloadMixItem(event, subPortfolio.id)}
																unstyled
																className="underline"
															>
																{(state) => (
																	<Text
																		type="Body/S/Link"
																		color={themeCSSVars.palette_N400}
																		className={{
																			"animate-pulse": state.kind === "loading",
																		}}
																	>
																		Composition
																	</Text>
																)}
															</Button>
														)}
													</li>
												)}
											</ForEach>
										</ul>
									</div>
								) : (
									<div>{downloadMenu}</div>
								))}
						</div>

						{variant === "collapsible" ? (
							<div className="flex flex-col h-[40px] self-end justify-end transition-[colors] relative z-0">
								<div
									className={toClassName({
										"absolute z-10 -bottom-1 inset-x-px pointer-events-none h-2 bg-[color:var(--palette-N20)]": true,
										hidden: !expandNote,
									})}
								/>
								<div
									className={toClassName({
										border: true,
										"border-transparent": !expandNote,
										"border-[var(--palette-N200)] !bg-[color:var(--palette-N20)]": expandNote,
									})}
								>
									{!notePayload && aclByArea.portfolio.canEditNotes(user.id, portfolio.richAcl?.acl ?? []) ? (
										<button
											type="button"
											className={toClassName({
												"px-1 py-2": true,
											})}
											onClick={() => {
												flushSync(() => {
													setExpandNote((e) => !e);
												});
												inlineFormHandlesRef.current?.focus();
											}}
											data-qualifier={`PortfolioHistory/Portfolio(${portfolio.uuid})/History(${event.id})/AddNote`}
										>
											<AddNoteIcon />
										</button>
									) : notePayload ? (
										<button
											type="button"
											style={{
												transitionDuration: `${transitionDurationMs}ms`,
												transform: `rotateX(${!expandNote ? 0 : 180}deg)`,
											}}
											className={toClassName({
												"px-1 py-2 transition-[opacity,transform]": true,
											})}
											onClick={() => {
												setExpandNote((e) => !e);
											}}
											data-qualifier={`PortfolioHistory/Portfolio(${portfolio.uuid})/History(${event.id})/EditNote`}
										>
											<NoteIcon />
										</button>
									) : (
										<></>
									)}
								</div>
							</div>
						) : (
							!notePayload?.note &&
							aclByArea.portfolio.canEditNotes(user.id, portfolio.richAcl?.acl ?? []) && (
								<div className="flex flex-col h-[40px] justify-end transition-[colors] relative z-0">
									<button
										type="button"
										className={toClassName({
											"px-1 py-2": true,
										})}
										onClick={() => {
											flushSync(() => {
												setShowExpandedEditor((e) => !e);
											});
											inlineFormHandlesRef.current?.focus();
										}}
										data-qualifier={`PortfolioHistory/Portfolio(${portfolio.uuid})/History(${event.id})/AddNote`}
									>
										<AddNoteIcon />
									</button>
								</div>
							)
						)}
					</div>
					{variant === "expanded" && (showExpandedEditor || notePayload?.note) && (
						<NoteInlineForm
							handleRef={inlineFormHandlesRef}
							className="pr-1"
							notePayload={notePayload}
							mode={
								showExpandedEditor && aclByArea.portfolio.canEditNotes(user.id, portfolio.richAcl?.acl ?? [])
									? "edit"
									: "view"
							}
							onEdit={onEdit}
							onDelete={onDelete}
							canEdit={aclByArea.portfolio.canEditNotes(user.id, portfolio.richAcl?.acl ?? [])}
						/>
					)}
				</div>
			</div>
			{variant === "collapsible" && (
				<div
					className={toClassName({
						"border-t": true,
						"border-transparent": !expandNote,
						"border-[var(--palette-N200)]": expandNote,
					})}
				>
					<NoteCollapsible
						handleRef={inlineFormHandlesRef}
						notePayload={notePayload}
						expand={expandNote}
						onEdit={onEdit}
						onDelete={onDelete}
						canEdit={aclByArea.portfolio.canEditNotes(user.id, portfolio.richAcl?.acl ?? [])}
					/>
				</div>
			)}
		</>
	);
}
function generateUserCirclePalette(params: {
	author: RichAccessControl;
	portfolio: InvestmentSummary;
	event: PortfolioHistoryEvent | NoteEvent;
	user: { id: string };
}) {
	if (isNoteEvent(params.event)) {
		return userCirclePaletteByRole.VIEWER(params.user.id, params.user.id);
	}

	return userCirclePaletteByRole[roleFromAcl("portfolio", params.author.userId!, params.portfolio.richAcl?.acl ?? [])](
		params.user.id,
		params.author.userId!,
	);
}

export type PortfolioHistoryListProps = {
	events: Array<PortfolioHistoryEvent | NoteEvent>;
	portfolio: InvestmentSummary;
	user: { id: string };
	onDownload?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadComposition?(event: PortfolioHistoryEvent, format: "xls" | "pdf"): Promise<void>;
	onDownloadMixItem?(event: PortfolioHistoryEvent, mixItemId: string): Promise<void>;
	onDownloadSphereFormat?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadUniverse?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadMarketView?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadReference?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadEditReference?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadBenchmark?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadEditBenchmark?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadEditMarketView?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadEditedSphere?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadEditUniverse?(event: PortfolioHistoryEvent): Promise<void>;
	onDownloadChanges?(event: PortfolioHistoryEvent): Promise<void>;
	variant: "collapsible" | "expanded";
	onEditNote: MaybeAsync<void, [/* historyItemId */ string, /* note */ string]>;
	onDeleteNote: MaybeAsync<void, [/* historyItemId */ string]>;
};

export function PortfolioHistoryList(props: PortfolioHistoryListProps): React.JSX.Element {
	const {
		user,
		portfolio,
		events,
		onDownloadComposition,
		onDownloadMixItem,
		variant,
		onEditNote,
		onDeleteNote,
		...downloads
	} = props;
	const eventGroupedByYear = useMemo(() => {
		const entries = Object.entries(groupBy(events, (event) => new Date(event.date).getFullYear().toString())).sort(
			builtInSortFnFor("0", "desc"),
		);
		entries.forEach(([, groupEvents]) => groupEvents?.sort(builtInSortFnFor("date", "desc")));
		return entries;
	}, [events]);
	return (
		<div className="w-full pr-1">
			<ForEach collection={eventGroupedByYear}>
				{([year, eventsEntry]) => (
					<div className="mb-4">
						<div className={`rounded-sm px-2 py-1 mb-1 bg-[var(--palette-N50)] ml-[36px] w-fit`}>
							<Text type="Body/L/Bold" as="span" color={themeCSSVars.palette_N400}>
								{year}
							</Text>
						</div>
						<div className="relative z-0 space-y-2">
							<div className="absolute z-0 -top-1 bottom-5 ml-[61px] border-r border-dashed border-[#A0A7B6]" />
							<ForEach collection={eventsEntry ?? []} keyProvider={(event, i) => event.id + event.date + i}>
								{(event) => (
									<div className="relative z-10">
										<EventViewer
											event={event}
											downloads={downloads}
											onDownloadComposition={onDownloadComposition}
											onDownloadMixItem={onDownloadMixItem}
											portfolio={portfolio}
											user={user}
											variant={variant}
											onEdit={(note) => onEditNote(event.id, note)}
											onDelete={() => onDeleteNote(event.id)}
										/>
									</div>
								)}
							</ForEach>
						</div>
					</div>
				)}
			</ForEach>
		</div>
	);
}

export function PortfolioHistoryBlockBase(
	props: Omit<PortfolioHistoryListProps, "variant" | "onEditNote" | "onDeleteNote"> & {
		onEditNote: MaybeAsync<(PortfolioHistoryEvent | NoteEvent)[], [/* historyItemId */ string, /* note */ string]>;
		onDeleteNote: MaybeAsync<(PortfolioHistoryEvent | NoteEvent)[], [/* historyItemId */ string]>;
	},
): React.JSX.Element {
	return (
		<div className="h-full flex flex-col">
			<ScrollWrapper direction="vertical" outerContainerAppearance={{ className: "flex-1" }}>
				<PortfolioHistoryList
					{...props}
					onDeleteNote={async (...params) => {
						await props.onDeleteNote(...params);
					}}
					onEditNote={async (...params) => {
						await props.onEditNote(...params);
					}}
					variant="collapsible"
				/>
			</ScrollWrapper>
			<div className={`flex justify-end pt-2 mt-1 border-t border-[var(--palette-N200)]`}>
				<Button
					palette="primary"
					onClick={() => {
						spawnPortfolioHistoryDialog(props);
					}}
				>
					View all
				</Button>
			</div>
		</div>
	);
}

function Spawned({
	events: initialEvents,
	onDeleteNote,
	onEditNote,
	...forward
}: Omit<PortfolioHistoryListProps, "variant" | "onEditNote" | "onDeleteNote"> & { zIndex?: number } & {
	onEditNote: MaybeAsync<PortfolioHistoryListProps["events"], [/* historyItemId */ string, /* note */ string]>;
	onDeleteNote: MaybeAsync<PortfolioHistoryListProps["events"], [/* historyItemId */ string]>;
}) {
	const [events, setEvents] = useState(initialEvents);
	return (
		<PortfolioHistoryList
			{...forward}
			events={events}
			variant="expanded"
			onDeleteNote={async (...params) => {
				setEvents(await onDeleteNote(...params));
			}}
			onEditNote={async (...params) => {
				setEvents(await onEditNote(...params));
			}}
		/>
	);
}

export function spawnPortfolioHistoryDialog(
	params: Omit<PortfolioHistoryListProps, "variant" | "onEditNote" | "onDeleteNote"> & {
		onEditNote: MaybeAsync<PortfolioHistoryListProps["events"], [/* historyItemId */ string, /* note */ string]>;
		onDeleteNote: MaybeAsync<PortfolioHistoryListProps["events"], [/* historyItemId */ string]>;
	},
): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<Dialog
				size="large"
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				header={`History & notes - ${params.portfolio.name ?? ""}`}
				footer={
					<DialogFooter
						neutralAction={
							<Button palette="tertiary" onClick={() => resolve()}>
								Close
							</Button>
						}
					/>
				}
			>
				<Spawned {...params} />
			</Dialog>
		)),
	);
}

export function AddNoteIcon(): React.JSX.Element {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M15.6399 14.8067V13.0958C15.6399 12.7665 15.9127 12.4999 16.2551 12.4999C16.5974 12.4999 16.8702 12.7665 16.8702 13.1063V14.8067H18.543C18.8723 14.8067 19.1389 15.0795 19.1389 15.4218C19.1389 15.7642 18.8723 16.037 18.5325 16.037H16.8702V18.0554C16.8702 18.3847 16.5921 18.6514 16.2551 18.6514C15.9181 18.6514 15.6399 18.3847 15.6399 18.0554V16.037H13.9293C13.6 16.037 13.3334 15.7588 13.3334 15.4218C13.3334 15.0848 13.6 14.8067 13.9293 14.8067H15.6399Z"
				fill="#8792AB"
			/>
			<path
				d="M16.6666 9.16667V6.0763C16.6666 5.6837 16.4908 5.30667 16.1783 5.02889L13.8216 2.93407C13.5091 2.6563 13.085 2.5 12.6433 2.5H4.99998C4.07915 2.5 3.33331 3.16296 3.33331 3.98148V14.3519C3.33331 15.1704 4.07915 15.8333 4.99998 15.8333H10.8333"
				stroke="#8792AB"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16.6667 6.2037H13.3333C12.8733 6.2037 12.5 5.87185 12.5 5.46296V2.5"
				stroke="#8792AB"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export function NoteIcon(): React.JSX.Element {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M9.99977 12.4584C9.88866 12.4584 9.78449 12.4409 9.68727 12.4059C9.59004 12.3714 9.49977 12.3125 9.41643 12.2292L5.56227 8.37502C5.40949 8.22224 5.33671 8.03113 5.34393 7.80169C5.3506 7.5728 5.43032 7.38197 5.5831 7.22919C5.73588 7.07641 5.93032 7.00002 6.16643 7.00002C6.40254 7.00002 6.59699 7.07641 6.74977 7.22919L9.99977 10.4792L13.2706 7.20835C13.4234 7.05558 13.6145 6.98252 13.8439 6.98919C14.0728 6.99641 14.2637 7.07641 14.4164 7.22919C14.5692 7.38197 14.6456 7.57641 14.6456 7.81252C14.6456 8.04863 14.5692 8.24308 14.4164 8.39585L10.5831 12.2292C10.4998 12.3125 10.4095 12.3714 10.3123 12.4059C10.215 12.4409 10.1109 12.4584 9.99977 12.4584Z"
				fill="#8792AB"
			/>
		</svg>
	);
}

export function aggregateHistory(history: InvestmentActionHistorySummaryEntry[]): PortfolioHistoryEvent[] {
	return history.map(
		(action): PortfolioHistoryEvent => ({
			date: action.applicationDate!,
			type: action.investmentActionHistory!,
			id: action.actionHistoryUuid ?? "",
			author: action.actionAuthor,
			marketViewEmbedded: action.marketViewEmbedded,
			note: !action.note
				? undefined
				: {
						author: action.note.author!,
						date: action.note.date!,
						note: action.note.text!,
						status: action.note.status!,
					},
			hasUniverseSnapshot: action.universeSnapshot,
			hasReferenceSnapshot: action.referenceSnapshot,
			hasBenchmarkSnapshot: action.benchmarkSnapshot,
			changesAvailable: action.changesAvailable,
			subPortfolios:
				(action.subPortfolios ?? []).length === 0
					? undefined
					: action.subPortfolios?.map((subPortfolios) => ({
							title: subPortfolios.name ?? "-",
							weight: subPortfolios.weight ?? 0,
							id: subPortfolios.uuid ?? "",
						})),
		}),
	);
}

const PortfolioHistoryBlock = (props: ContextContent<typeof PortfolioContext>) => {
	const uuid = props.portfolio?.uuid;
	const exportApi = useApiGen(InvestmentExportControllerApiFactory);
	const investmentReportApi = useApiGen(InvestmentReportsControllerApiFactory);
	const user = useUserValue();

	const { t } = useTranslation();

	const query = useQueryNoRefetch(["History", uuid, props.asOf], {
		queryFn: async (): Promise<PortfolioWidgetData<PortfolioHistoryEvent[] | undefined>> => {
			if (!uuid) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "PortfolioHistoryBlock"),
				};
			}

			const { data: historyData } = await investmentReportApi.getInvestmentActionHistorySummary(uuid, props.asOf);
			if (!historyData) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "PortfolioHistoryBlock"),
				};
			}

			const events = aggregateHistory(historyData);
			// A history that arrives with no entry in it has nothing to show.
			if (events.length === 0) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: events,
				widgetStatus: WidgetStatus.READY,
			};
		},
		enabled: isAsOfReadable(props.asOf, props.asOfSnapshot),
	});

	// Download — mirrors the visible timeline via the smart-workspace `investmentHistoryAction` data provider.
	const exportHistory = async (investmentUuid: string) => {
		const config = buildWidgetExportConfig("PortfolioHistoryBlock", { investmentUuid }, { asOf: props.asOf });
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export portfolio history",
					payload: JSON.stringify(config),
				},
			},
		);
	};
	const exportHistoryRef = useUnsafeUpdatedRef(exportHistory);

	const canDownload = Boolean(uuid && query.data?.widgetStatus === WidgetStatus.READY);

	useWidgetOptions(
		() => ({
			tooltip: t("PORTFOLIO_HISTORY.TOOLTIP"),
			title: t("PORTFOLIO_HISTORY.TITLE"),
			actionHeader: canDownload ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioHistory.downloadButton }}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportHistoryRef.current(uuid);
							},
							label: t("PORTFOLIO_HISTORY.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioHistory.downloadItem,
						},
					]}
				/>
			) : null,
		}),
		[t, canDownload, uuid, exportHistoryRef],
	);

	// Legacy blob export of a history entry's composition, used for every entry that is not a plain
	// "Composition" download (e.g. "Composition & trades").
	const downloadHistoryCompositionEntry = useCallback(
		async (entry: { id: string }) => {
			const response = await exportApi.exportHistoryCompositionEntry(uuid ?? "", entry.id, undefined, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	// Column-aware export of a history entry's composition through the smart-workspace data provider,
	// mirroring the composition table's visible columns and order.
	const downloadCompositionViaWorkspace = useCallback(
		async (entry: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const config = buildCompositionExportConfig("INVESTMENT_HISTORY", {
				investmentUuid: uuid,
				snapshot: "CURRENT",
				colStateKey: persistedColStateKeys["tableV3-investment-composition-read"],
				history: entry.id,
				quantityMode: props.portfolio.quantityMode,
			});
			const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
			await downloadContentDisposition(file);
		},
		[uuid, props.portfolio.quantityMode],
	);

	const handleDownload = useCallback(
		async (event: PortfolioHistoryEvent) =>
			typeToStringMap[event.type]?.downloadText === "Composition"
				? downloadCompositionViaWorkspace(event)
				: downloadHistoryCompositionEntry(event),
		[downloadCompositionViaWorkspace, downloadHistoryCompositionEntry],
	);

	const handleDownloadSphereFormat = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryPortfolio(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadUniverse = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryUniverse(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadMixItem = useCallback(
		async (entry: { id: string }, mixItemId: string) => {
			const response = await exportApi.exportHistoryCompositionEntry(uuid ?? "", entry.id, mixItemId, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const exposureReportName = "Composition_and_exposure_contribution";
	const handleDownloadComposition = useCallback(
		(entry: { id: string }, format: "xls" | "pdf") => {
			switch (format) {
				case "xls":
					return downloadHistoryCompositionEntry(entry);
				case "pdf":
					return downloadSinglePdf(exposureReportName, {
						templateId: "mixed-portfolio" as DefaultTemplateId,
						historyEventUuid: encodeURIComponent(entry.id),
						objectId: uuid,
						name: exposureReportName,
					});
			}
		},
		[downloadHistoryCompositionEntry, uuid],
	);

	const handleDownloadReference = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryReference(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadBenchmark = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryBenchmark(uuid, event.id, {
				responseType: "blob",
			});
			await downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadMarketView = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryMarketView(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadEditUniverse = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportEditHistoryUniverse(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadEditReference = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportEditReferenceInPortfolio(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadEditBenchmark = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportEditBenchmarkInPortfolio(uuid, event.id, {
				responseType: "blob",
			});
			await downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadChanges = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryChanges(uuid, event.id, {
				responseType: "blob",
			});
			await downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadEditMarketView = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportHistoryMarketView(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	const handleDownloadEditedSphere = useCallback(
		async (event: PortfolioHistoryEvent) => {
			if (!uuid) {
				throw new Error("Missing uuid");
			}
			const response = await exportApi.exportEditPortfolio(uuid, event.id, {
				responseType: "blob",
			});
			downloadContentDisposition(response);
		},
		[exportApi, uuid],
	);

	return (
		<PortfolioQueryWidgetBase query={query}>
			{(events, rq) => (
				<PortfolioHistoryBlockBase
					onDownload={handleDownload}
					onDownloadComposition={handleDownloadComposition}
					onDownloadMixItem={handleDownloadMixItem}
					onDownloadSphereFormat={handleDownloadSphereFormat}
					onDownloadUniverse={handleDownloadUniverse}
					onDownloadEditReference={handleDownloadEditReference}
					onDownloadEditMarketView={handleDownloadEditMarketView}
					onDownloadEditUniverse={handleDownloadEditUniverse}
					onDownloadEditedSphere={handleDownloadEditedSphere}
					onDownloadMarketView={handleDownloadMarketView}
					onDownloadReference={handleDownloadReference}
					onDownloadBenchmark={handleDownloadBenchmark}
					onDownloadEditBenchmark={handleDownloadEditBenchmark}
					onDownloadChanges={handleDownloadChanges}
					events={events}
					portfolio={props.portfolio}
					user={user}
					onEditNote={async (historyItemId, note) => {
						await portfolioRepository.editHistoryEntryNote(props.portfolio.uuid, {
							uuid: historyItemId,
							note,
						});
						return (await rq.refetch()).data?.data ?? stableEmptyArray;
					}}
					onDeleteNote={async (historyItemId) => {
						const shouldDelete = await spawnYesNoDialog({
							header: "Are you sure you want to delete this item?",
							children: (
								<p>This will permanently remove the note.{"\n"}Please be aware that this action cannot be undone.</p>
							),
						});
						if (shouldDelete) {
							await portfolioRepository.deleteHistoryEntryNote(props.portfolio.uuid, {
								uuid: historyItemId,
							});
							return aggregateHistory((await rq.refetch()).data?.data ?? stableEmptyArray);
						}
						return events;
					}}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
};

export default withContext(PortfolioContext)(PortfolioHistoryBlock);
